import { useCallback } from 'react';
import { getSoftwareDownloadUrl } from 'services/api/client';
import { trackEvent, PixumEnvUtils, trackDownloadSoftware, getValidLocaleISO, isBrowser, logger } from 'utils';

/**
 * Custom hook for triggering software downloads via PSI service.
 *
 * Session and user info are initialized server-side via streaming RSC before
 * client hydration, so no gating on sessionInitialized / isUserInfoReady is required.
 *
 * @param operatingSystem - The user's operating system (Windows, macOS, Linux, etc.)
 * @returns A function that triggers the software download when called
 */
export function useSoftwareDownload(operatingSystem: string) {
  const triggerDownload = useCallback(async () => {
    try {
      const locale = getValidLocaleISO(PixumEnvUtils.get('LOCALE'));
      const countryUrl = PixumEnvUtils.get('COUNTRY_URL') || '';

      const googleClickId = isBrowser() ? new URLSearchParams(window.location.search).get('gclid') : null;

      const jentisCache = isBrowser() ? window.jentis?.cache : null;
      const clientId = jentisCache?.ga_fe_cid;

      const downloadUrl = await getSoftwareDownloadUrl({
        operatingSystem,
        locale,
        countryUrl,
        googleClickId: googleClickId || undefined,
        clientId: clientId || undefined,
      });

      trackEvent('Download', 'Fotobuch', operatingSystem);
      trackDownloadSoftware();

      if (clientId && window.adtriba) {
        window.adtriba.push(['conversion', 'Download', { id: clientId }]);
      }

      const downloadIframe = document.createElement('iframe');

      downloadIframe.setAttribute('src', downloadUrl);
      downloadIframe.style.display = 'none';
      document.body.appendChild(downloadIframe);
    } catch (error) {
      logger.error({ error }, 'Failed to download software');
    }
  }, [operatingSystem]);

  return triggerDownload;
}
